import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Modal,
  Tag,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip,
  Typography,
  Empty,
  Rate,
  Descriptions,
  Divider,
  Input,
  Select,
  message,
  Form
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  TrophyOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as actions from 'store/actions/AuthActions';
import useIsMobile from 'hooks/useIsMobile';
import MobileFilterDrawer, { MobileFilterButton } from 'components/Common/MobileFilterDrawer';
import useUrlFilters from 'hooks/useUrlFilters';

const { Text, Title } = Typography;
const { Option } = Select;

// Applied filters live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  status: { type: 'string' },
  reviewType: { type: 'string', key: 'review_type' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};
const URL_FILTER_NAMES = { search: 'search', status: 'status', review_type: 'reviewType' };

const PerformanceReviews = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const pageSize = Math.min(urlFilters.pageSize, MAX_PAGE_SIZE);
  const appliedFilters = useMemo(() => ({
    search: urlFilters.search || '',
    status: urlFilters.status,
    review_type: urlFilters.reviewType,
  }), [urlFilters]);
  // Local copy holds the typed-but-not-submitted search text
  const [filters, setFilters] = useState(appliedFilters);
  useEffect(() => setFilters(appliedFilters), [appliedFilters]);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status) count++;
    if (filters.review_type) count++;
    return count;
  };

  // Fetch reviews
  const fetchReviews = async (page = 1, pageSize = 10, currentFilters = appliedFilters) => {
    try {
      setLoading(true);
      const response = await actions.loadEmployeeReviews(page, pageSize, currentFilters);
      if (response) {
        setReviews(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      message.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReviews(urlFilters.page, pageSize, appliedFilters);
  }, [urlFilters, appliedFilters]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'SUBMITTED':
        return 'processing';
      case 'ACKNOWLEDGED':
        return 'success';
      case 'COMPLETED':
        return 'green';
      default:
        return 'default';
    }
  };

  // Get rating description
  const getRatingDescription = (rating) => {
    const descriptions = {
      1: 'Needs Improvement',
      2: 'Below Expectations',
      3: 'Meets Expectations',
      4: 'Exceeds Expectations',
      5: 'Outstanding'
    };
    return descriptions[rating] || '-';
  };

  // Handle view review
  const handleViewReview = async (record) => {
    try {
      const fullReview = await actions.fetchEmployeeReview(record.id);
      if (fullReview) {
        setSelectedReview(fullReview);
        setViewModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching review details:', error);
      message.error('Failed to load review details');
    }
  };

  // Handle edit review
  const handleEditReview = (record) => {
    navigate(`/hr/performance-reviews/edit/${record.id}`);
  };

  // Handle delete review
  const handleDeleteReview = async () => {
    try {
      await actions.deleteEmployeeReview(selectedReview.id);
      message.success('Review deleted successfully');
      setDeleteModalVisible(false);
      fetchReviews(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error deleting review:', error);
      message.error('Failed to delete review');
    }
  };

  // Handle submit review
  const handleSubmitReview = async (record) => {
    try {
      await actions.submitReview(record.id);
      message.success('Review submitted successfully');
      fetchReviews(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error submitting review:', error);
      message.error('Failed to submit review');
    }
  };

  // Handle complete review
  const handleCompleteReview = async (record) => {
    try {
      await actions.completeReview(record.id);
      message.success('Review marked as completed');
      fetchReviews(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error completing review:', error);
      message.error('Failed to complete review');
    }
  };

  // Handle table pagination change
  const handleTableChange = (newPagination) => {
    const sizeChanged = newPagination.pageSize !== pageSize;
    setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setUrlFilters({ [URL_FILTER_NAMES[key]]: value, page: 1 });
  };

  // Handle search
  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ search: '', status: null, review_type: null });
    setUrlFilters({ search: null, status: null, reviewType: null, page: 1 });
  };

  // Calculate statistics
  const getReviewStats = () => {
    const draft = reviews.filter(r => r.status === 'DRAFT').length;
    const submitted = reviews.filter(r => r.status === 'SUBMITTED').length;
    const acknowledged = reviews.filter(r => r.status === 'ACKNOWLEDGED').length;
    const completed = reviews.filter(r => r.status === 'COMPLETED').length;
    return { draft, submitted, acknowledged, completed, total: pagination.total };
  };

  const stats = getReviewStats();

  // Table columns
  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee',
      width: 180,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || '-'}</Text>
          {record.title && <Text type="secondary" style={{ fontSize: '12px' }}>{record.title}</Text>}
        </Space>
      )
    },
    {
      title: 'Review Type',
      dataIndex: 'review_type_display',
      key: 'review_type',
      width: 130,
      render: (text, record) => text || record.review_type
    },
    {
      title: 'Period',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <span>
          {dayjs(record.review_period_start).format('MMM DD, YYYY')} - {dayjs(record.review_period_end).format('MMM DD, YYYY')}
        </span>
      )
    },
    {
      title: 'Reviewer',
      dataIndex: 'reviewer_name',
      key: 'reviewer',
      width: 150,
      render: (text) => text || <Text type="secondary">-</Text>
    },
    {
      title: 'Rating',
      dataIndex: 'overall_rating',
      key: 'rating',
      width: 150,
      render: (rating) => rating ? (
        <Space>
          <Rate disabled defaultValue={rating} style={{ fontSize: '14px' }} />
          <Text type="secondary">({rating}/5)</Text>
        </Space>
      ) : <Text type="secondary">Not rated</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => (
        <Tag color={getStatusColor(status)}>
          {record.status_display || status}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewReview(record)}
            />
          </Tooltip>
          {record.status === 'DRAFT' && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditReview(record)}
                />
              </Tooltip>
              <Tooltip title="Submit Review">
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  style={{ color: '#1890ff' }}
                  onClick={() => handleSubmitReview(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setSelectedReview(record);
                    setDeleteModalVisible(true);
                  }}
                />
              </Tooltip>
            </>
          )}
          {record.status === 'ACKNOWLEDGED' && (
            <Tooltip title="Mark as Completed">
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleCompleteReview(record)}
              >
                Complete
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // Render review details modal content
  const renderReviewDetails = () => {
    if (!selectedReview) return null;

    return (
      <div>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Employee" span={1}>
            {selectedReview.employee_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={1}>
            <Tag color={getStatusColor(selectedReview.status)}>
              {selectedReview.status_display || selectedReview.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Review Type" span={1}>
            {selectedReview.review_type_display || selectedReview.review_type}
          </Descriptions.Item>
          <Descriptions.Item label="Reviewer" span={1}>
            {selectedReview.reviewer_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Review Period" span={2}>
            {dayjs(selectedReview.review_period_start).format('MMMM DD, YYYY')} - {dayjs(selectedReview.review_period_end).format('MMMM DD, YYYY')}
          </Descriptions.Item>
          {selectedReview.title && (
            <Descriptions.Item label="Title" span={2}>
              {selectedReview.title}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider>Ratings</Divider>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Overall Rating"
                value={selectedReview.overall_rating || '-'}
                suffix={selectedReview.overall_rating ? '/5' : ''}
                valueStyle={{ color: '#1890ff' }}
                prefix={<TrophyOutlined />}
              />
              {selectedReview.overall_rating && (
                <Text type="secondary">{getRatingDescription(selectedReview.overall_rating)}</Text>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Performance"
                value={selectedReview.performance_rating || '-'}
                suffix={selectedReview.performance_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Communication"
                value={selectedReview.communication_rating || '-'}
                suffix={selectedReview.communication_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Teamwork"
                value={selectedReview.teamwork_rating || '-'}
                suffix={selectedReview.teamwork_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Punctuality"
                value={selectedReview.punctuality_rating || '-'}
                suffix={selectedReview.punctuality_rating ? '/5' : ''}
              />
            </Card>
          </Col>
        </Row>

        <Divider>Feedback</Divider>

        {selectedReview.strengths && (
          <Card size="small" title="Strengths" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.strengths}</p>
          </Card>
        )}

        {selectedReview.areas_for_improvement && (
          <Card size="small" title="Areas for Improvement" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.areas_for_improvement}</p>
          </Card>
        )}

        {selectedReview.goals && (
          <Card size="small" title="Goals" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.goals}</p>
          </Card>
        )}

        {selectedReview.reviewer_comments && (
          <Card size="small" title="Reviewer Comments" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.reviewer_comments}</p>
          </Card>
        )}

        {selectedReview.employee_comments && (
          <Card size="small" title="Employee Comments" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.employee_comments}</p>
          </Card>
        )}

        {selectedReview.submitted_at && (
          <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
            Submitted on: {dayjs(selectedReview.submitted_at).format('MMMM DD, YYYY hh:mm A')}
          </Text>
        )}

        {selectedReview.acknowledged_at && (
          <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
            Acknowledged on: {dayjs(selectedReview.acknowledged_at).format('MMMM DD, YYYY hh:mm A')}
          </Text>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: '8px' }} />
            Performance Reviews
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/hr/performance-reviews/new')}
          >
            Create Review
          </Button>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Draft"
              value={stats.draft}
              valueStyle={{ color: '#8c8c8c' }}
              prefix={<EditOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Submitted"
              value={stats.submitted}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SendOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Acknowledged"
              value={stats.acknowledged}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card
        style={{ marginBottom: '24px' }}
        size={isMobile ? 'small' : 'default'}
        extra={
          isMobile && (
            <MobileFilterButton
              onClick={() => setFilterDrawerVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
              size="small"
            />
          )
        }
      >
        {/* Desktop Filters */}
        {!isMobile && (
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder="Search by employee or title..."
                allowClear
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onSearch={handleSearch}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Status"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="DRAFT">Draft</Option>
                <Option value="SUBMITTED">Submitted</Option>
                <Option value="ACKNOWLEDGED">Acknowledged</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Type"
                allowClear
                style={{ width: '100%' }}
                value={filters.review_type}
                onChange={(value) => handleFilterChange('review_type', value)}
              >
                <Option value="MONTHLY">Monthly Review</Option>
                <Option value="CUSTOM">Custom Review</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button onClick={clearFilters}>Clear Filters</Button>
            </Col>
          </Row>
        )}

        {/* Mobile Filter Drawer */}
        <MobileFilterDrawer
          visible={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          onClear={clearFilters}
          title="Review Filters"
          activeFiltersCount={getActiveFiltersCount()}
        >
          <Form layout="vertical">
            <Form.Item label="Search">
              <Input.Search
                placeholder="Search by employee or title..."
                allowClear
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onSearch={handleSearch}
                prefix={<SearchOutlined />}
              />
            </Form.Item>
            <Form.Item label="Status">
              <Select
                placeholder="Filter by Status"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="DRAFT">Draft</Option>
                <Option value="SUBMITTED">Submitted</Option>
                <Option value="ACKNOWLEDGED">Acknowledged</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Review Type">
              <Select
                placeholder="Filter by Type"
                allowClear
                style={{ width: '100%' }}
                value={filters.review_type}
                onChange={(value) => handleFilterChange('review_type', value)}
              >
                <Option value="MONTHLY">Monthly Review</Option>
                <Option value="CUSTOM">Custom Review</Option>
              </Select>
            </Form.Item>
          </Form>
        </MobileFilterDrawer>
      </Card>

      {/* Reviews Table */}
      <Card size={isMobile ? 'small' : 'default'}>
        <Table
          columns={columns}
          dataSource={reviews}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: !isMobile ? (total, range) => `${range[0]}-${range[1]} of ${total} reviews` : undefined,
            simple: isMobile,
            size: isMobile ? 'small' : 'default',
          }}
          onChange={handleTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: (
              <Empty
                description="No reviews found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={() => navigate('/hr/performance-reviews/new')}>
                  Create First Review
                </Button>
              </Empty>
            )
          }}
        />
      </Card>

      {/* View Review Modal */}
      <Modal
        title="Review Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
          selectedReview?.status === 'DRAFT' && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                setViewModalVisible(false);
                handleEditReview(selectedReview);
              }}
            >
              Edit Review
            </Button>
          )
        ].filter(Boolean)}
        width={800}
      >
        {renderReviewDetails()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Review"
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDeleteReview}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this review?</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default PerformanceReviews;
